import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, Phone, Mail, ShieldCheck, Pencil, Save, X, Plus, Trash2, Archive as ArchiveIcon, RotateCcw, KeyRound, Home, FileText, ChevronDown, ChevronRight, Users, FolderOpen } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import ContactsPanel from '../../components/pm/ContactsPanel';
import DocumentsPanel from '../../components/pm/DocumentsPanel';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { pmApi } from '../../lib/pmApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PM_ADMIN_ROLES = ['property_manager', 'asset_manager', 'maintenance_supervisor'];

const Field = ({ label, value, editing, onChange, type = 'text', testid, placeholder = '' }) => (
  <div>
    <Label className="text-[#A3A3A3] text-xs uppercase tracking-wider">{label}</Label>
    {editing ? (
      type === 'textarea' ? (
        <Textarea data-testid={testid} rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder={placeholder} />
      ) : (
        <Input data-testid={testid} type={type} value={value || ''} onChange={(e) => onChange(type === 'number' ? e.target.value : e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder={placeholder} />
      )
    ) : (
      <p className="text-white mt-1 break-words">{value || <span className="text-[#525252]">—</span>}</p>
    )}
  </div>
);

const PropertyDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = PM_ADMIN_ROLES.includes(user?.role);

  const [p, setP] = useState(null);
  const [requests, setRequests] = useState([]);
  const [units, setUnits] = useState([]);
  const [err, setErr] = useState('');

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [showUnits, setShowUnits] = useState(true);
  const [unitDialog, setUnitDialog] = useState(null); // { mode: 'add'|'edit', unit: {...} }
  const [archiveOpen, setArchiveOpen] = useState(false);

  const load = async () => {
    try {
      // Prefer the self-edit shape so we get gate_code / billing fields cleanly
      const { data: list } = await axios.get(`${API}/pm/properties`, { withCredentials: true });
      const arr = Array.isArray(list) ? list : (list.items || []);
      const found = arr.find((x) => x.id === id);
      if (found) {
        setP(found);
      } else {
        // Fall back to admin detail endpoint
        const detail = await pmApi.propertyDetail(id);
        setP(detail);
      }
      setDraft({});
      // Units
      try {
        const { data: u } = await axios.get(`${API}/pm/properties/${id}/units`, { withCredentials: true });
        setUnits(u.items || []);
      } catch { setUnits([]); }
      // Recent requests
      pmApi.listRequests({ property_id: id }).then(setRequests).catch(() => {});
    } catch (e) {
      setErr(e.response?.data?.detail?.code || e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: list } = await axios.get(`${API}/pm/properties`, { withCredentials: true });
        if (!alive) return;
        const arr = Array.isArray(list) ? list : (list.items || []);
        const found = arr.find((x) => x.id === id);
        if (found) {
          setP(found);
        } else {
          const detail = await pmApi.propertyDetail(id);
          if (!alive) return;
          setP(detail);
        }
        setDraft({});
        try {
          const { data: u } = await axios.get(`${API}/pm/properties/${id}/units`, { withCredentials: true });
          if (alive) setUnits(u.items || []);
        } catch { if (alive) setUnits([]); }
        pmApi.listRequests({ property_id: id }).then((r) => { if (alive) setRequests(r); }).catch(() => {});
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail?.code || e.response?.data?.detail || e.message);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const startEdit = () => {
    setDraft({
      name: p.name, address: p.address, city: p.city, state: p.state, zip: p.zip,
      unit_count: p.unit_count, gate_code: p.gate_code || '', lockbox_code: p.lockbox_code || '',
      access_instructions: p.access_instructions || '',
      billing_contact_name: p.billing_contact_name || '',
      billing_contact_email: p.billing_contact_email || '',
      billing_contact_phone: p.billing_contact_phone || '',
      notes_pm: p.notes_pm || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const body = { ...draft };
      Object.keys(body).forEach((k) => { if (typeof body[k] === 'string') body[k] = body[k].trim(); });
      if (!body.billing_contact_email) delete body.billing_contact_email;
      if (body.unit_count !== undefined && body.unit_count !== null && body.unit_count !== '') {
        body.unit_count = Number(body.unit_count);
      }
      const { data } = await axios.put(`${API}/pm/properties/${id}`, body, { withCredentials: true });
      setP(data);
      setEditing(false);
      toast.success(`Saved — now v${data.version}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => `${(x.loc || []).slice(-1)[0]}: ${x.msg}`).join(' · ') : (d?.code || d || e.message);
      toast.error(`Save failed: ${msg}`);
    }
    setBusy(false);
  };

  const archive = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/pm/properties/${id}`, { withCredentials: true });
      toast.success('Property archived');
      setArchiveOpen(false);
      nav('/pm/properties');
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Archive failed');
    }
    setBusy(false);
  };

  const restore = async () => {
    try {
      await axios.post(`${API}/pm/properties/${id}/restore`, {}, { withCredentials: true });
      toast.success('Property restored');
      load();
    } catch (e) {
      toast.error('Restore failed');
    }
  };

  const saveUnit = async () => {
    if (!unitDialog?.unit?.unit_number?.trim()) {
      toast.error('Unit number required');
      return;
    }
    const u = { ...unitDialog.unit };
    if (!u.tenant_email) delete u.tenant_email;
    ['bedrooms', 'bathrooms', 'sqft'].forEach((k) => {
      if (u[k] === '' || u[k] === null || u[k] === undefined) delete u[k];
      else u[k] = Number(u[k]);
    });
    setBusy(true);
    try {
      if (unitDialog.mode === 'add') {
        await axios.post(`${API}/pm/properties/${id}/units`, u, { withCredentials: true });
        toast.success('Unit added');
      } else {
        await axios.put(`${API}/pm/properties/${id}/units/${u.id}`, u, { withCredentials: true });
        toast.success('Unit updated');
      }
      setUnitDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Unit save failed');
    }
    setBusy(false);
  };

  const archiveUnit = async (unit) => {
    if (!window.confirm(`Archive unit ${unit.unit_number}?`)) return;
    try {
      await axios.delete(`${API}/pm/properties/${id}/units/${unit.id}`, { withCredentials: true });
      toast.success('Unit archived');
      load();
    } catch (e) {
      toast.error('Archive failed');
    }
  };

  if (err) return <div className="p-6 text-[#EF4444]" data-testid="prop-detail-error">{err}</div>;
  if (!p) return <div className="p-6 text-[#A3A3A3]">Loading…</div>;

  const isArchived = (p.archive_status || 'active') === 'archived';

  return (
    <div data-testid="pm-property-detail" className="p-6 space-y-6 max-w-5xl">
      <Link to="/pm/properties" className="text-[#A3A3A3] text-sm hover:text-white inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> All properties</Link>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">{p.company_name || 'PROPERTY'}</p>
          <h1 className="font-['Outfit'] text-3xl font-bold text-white flex items-center gap-3">
            {p.name}
            {isArchived && <Badge className="bg-[#262626] text-[#A3A3A3] border-[#404040]">ARCHIVED</Badge>}
            <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30">v{p.version || '1.0'}</Badge>
          </h1>
          <p className="text-[#525252] text-sm font-mono mt-1">{p.property_code}</p>
          {p.last_published_by_name && <p className="text-[#525252] text-xs">Last updated by {p.last_published_by_name}</p>}
        </div>
        {isAdmin && !editing && (
          <div className="flex flex-wrap items-center gap-2">
            {!isArchived ? (
              <>
                <Button data-testid="edit-property-btn" onClick={startEdit} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]"><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
                <Button data-testid="archive-property-btn" variant="outline" className="border-red-500/50 text-red-300 hover:bg-red-500/10" onClick={() => setArchiveOpen(true)}><ArchiveIcon className="w-4 h-4 mr-1" /> Archive</Button>
              </>
            ) : (
              <Button data-testid="restore-property-btn" variant="outline" className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10" onClick={restore}><RotateCcw className="w-4 h-4 mr-1" /> Restore</Button>
            )}
          </div>
        )}
        {editing && (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-[#404040] text-white" onClick={() => { setEditing(false); setDraft({}); }}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button data-testid="save-property-btn" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]" disabled={busy} onClick={saveEdit}><Save className="w-4 h-4 mr-1" /> Save</Button>
          </div>
        )}
      </div>

      {/* Tabs: Details / Contacts / Documents */}
      <Tabs defaultValue="details" className="w-full">
        <TabsList className="bg-[#141414] border border-[#262626]">
          <TabsTrigger value="details" data-testid="tab-details" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]">Details</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]"><Users className="w-3.5 h-3.5 mr-1" /> Contacts</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]"><FolderOpen className="w-3.5 h-3.5 mr-1" /> Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6 mt-4">

      {/* Address + Access */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3">
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em]"><MapPin className="w-3 h-3 inline mr-1" /> ADDRESS</p>
          <Field label="Property name" value={editing ? draft.name : p.name} editing={editing} onChange={(v) => setDraft({ ...draft, name: v })} testid="edit-name" />
          <Field label="Street" value={editing ? draft.address : p.address} editing={editing} onChange={(v) => setDraft({ ...draft, address: v })} testid="edit-address" />
          <div className="grid grid-cols-3 gap-2">
            <Field label="City" value={editing ? draft.city : p.city} editing={editing} onChange={(v) => setDraft({ ...draft, city: v })} testid="edit-city" />
            <Field label="State" value={editing ? draft.state : p.state} editing={editing} onChange={(v) => setDraft({ ...draft, state: v })} testid="edit-state" />
            <Field label="ZIP" value={editing ? draft.zip : p.zip} editing={editing} onChange={(v) => setDraft({ ...draft, zip: v })} testid="edit-zip" />
          </div>
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3">
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em]"><KeyRound className="w-3 h-3 inline mr-1" /> ACCESS</p>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Gate code" value={editing ? draft.gate_code : p.gate_code} editing={editing} onChange={(v) => setDraft({ ...draft, gate_code: v })} testid="edit-gate" />
            <Field label="Lockbox" value={editing ? draft.lockbox_code : p.lockbox_code} editing={editing} onChange={(v) => setDraft({ ...draft, lockbox_code: v })} testid="edit-lockbox" />
          </div>
          <Field label="Access instructions" value={editing ? draft.access_instructions : p.access_instructions} editing={editing} onChange={(v) => setDraft({ ...draft, access_instructions: v })} testid="edit-access" type="textarea" placeholder="e.g. Front gate code at right panel, dogs in unit 5B…" />
        </div>
      </div>

      {/* Billing contact + PM notes */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3">
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em]"><Mail className="w-3 h-3 inline mr-1" /> BILLING CONTACT</p>
          <Field label="Name" value={editing ? draft.billing_contact_name : p.billing_contact_name} editing={editing} onChange={(v) => setDraft({ ...draft, billing_contact_name: v })} testid="edit-bill-name" />
          <Field label="Email" type="email" value={editing ? draft.billing_contact_email : p.billing_contact_email} editing={editing} onChange={(v) => setDraft({ ...draft, billing_contact_email: v })} testid="edit-bill-email" />
          <Field label="Phone" value={editing ? draft.billing_contact_phone : p.billing_contact_phone} editing={editing} onChange={(v) => setDraft({ ...draft, billing_contact_phone: v })} testid="edit-bill-phone" />
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3">
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em]"><FileText className="w-3 h-3 inline mr-1" /> PROPERTY NOTES <span className="text-[#525252] font-normal text-[10px] tracking-normal">(PM-visible)</span></p>
          <Field label="Notes" value={editing ? draft.notes_pm : p.notes_pm} editing={editing} onChange={(v) => setDraft({ ...draft, notes_pm: v })} testid="edit-notes-pm" type="textarea" placeholder="e.g. Recurring root issue in main line, shutoff behind hedges…" />
          {typeof p.effective_approval_limit === 'number' && p.effective_approval_limit > 0 && (
            <p data-testid="pm-approval-limit" className="text-[#A3A3A3] text-sm flex items-center gap-2 pt-2 border-t border-[#262626]"><ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> Auto-approval limit: <span className="text-white font-medium">${p.effective_approval_limit.toLocaleString()}</span></p>
          )}
        </div>
      </div>

      {/* Units */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-5">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setShowUnits((s) => !s)} className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] flex items-center gap-1">
            {showUnits ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} <Home className="w-3 h-3" /> UNITS ({units.length})
          </button>
          {isAdmin && !isArchived && (
            <Button data-testid="add-unit-btn" size="sm" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]" onClick={() => setUnitDialog({ mode: 'add', unit: { unit_number: '', bedrooms: '', bathrooms: '', sqft: '', occupied: false, tenant_name: '', tenant_phone: '', tenant_email: '', notes: '' } })}><Plus className="w-3 h-3 mr-1" /> Add Unit</Button>
          )}
        </div>
        {showUnits && (
          <div className="space-y-2">
            {units.length === 0 ? (
              <p className="text-[#525252] text-sm">No units yet.</p>
            ) : units.map((u) => (
              <div key={u.id} data-testid={`unit-row-${u.id}`} className="flex items-start justify-between gap-3 p-3 bg-[#0A0A0A] border border-[#262626] rounded">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-medium">Unit {u.unit_number}</span>
                    {u.occupied && <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Occupied</Badge>}
                    <Badge className="bg-[#262626] text-[#A3A3A3] border-[#404040]">v{u.version}</Badge>
                  </div>
                  <p className="text-[#A3A3A3] text-xs mt-1">
                    {[u.bedrooms ? `${u.bedrooms}bd` : null, u.bathrooms ? `${u.bathrooms}ba` : null, u.sqft ? `${u.sqft}sqft` : null].filter(Boolean).join(' · ') || 'no details'}
                  </p>
                  {u.tenant_name && <p className="text-white text-sm mt-1">Tenant: {u.tenant_name}{u.tenant_phone ? ` · ${u.tenant_phone}` : ''}</p>}
                </div>
                {isAdmin && !isArchived && (
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" className="border-[#404040] text-white h-7" data-testid={`edit-unit-${u.id}`} onClick={() => setUnitDialog({ mode: 'edit', unit: { ...u } })}><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" className="border-red-500/50 text-red-300 hover:bg-red-500/10 h-7" data-testid={`archive-unit-${u.id}`} onClick={() => archiveUnit(u)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent service requests */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em]">RECENT REQUESTS</p>
          <Link to={`/pm/submit?property=${p.id}`} className="text-[#D4AF37] text-sm hover:text-[#F3E5AB]">+ Submit request</Link>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded-md overflow-hidden">
          {requests.slice(0, 8).map((r) => (
            <Link key={r.id} to={`/pm/work-orders/${r.id}`} className="flex items-center justify-between px-4 py-3 border-b border-[#262626] last:border-0 hover:bg-[#1a1a1a]">
              <div>
                <p className="text-white text-sm font-mono">{r.work_order_number}</p>
                <p className="text-[#A3A3A3] text-xs">{r.issue_description?.slice(0, 80)}</p>
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded ${r.priority === 'Emergency' ? 'bg-[#EF4444]/15 text-[#EF4444]' : 'bg-[#262626] text-[#A3A3A3]'}`}>{r.priority}</span>
            </Link>
          ))}
          {requests.length === 0 && <p className="p-6 text-center text-[#525252] text-sm">No requests for this property yet.</p>}
        </div>
      </div>

        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <ContactsPanel propertyId={id} canEdit={isAdmin && !isArchived} />
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <DocumentsPanel propertyId={id} canEdit={isAdmin && !isArchived} />
        </TabsContent>
      </Tabs>

      {/* Unit add/edit dialog */}
      <Dialog open={!!unitDialog} onOpenChange={(o) => { if (!o) setUnitDialog(null); }}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{unitDialog?.mode === 'add' ? 'Add unit' : `Edit unit ${unitDialog?.unit?.unit_number}`}</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">Saved immediately. Prior state snapshotted for rollback.</DialogDescription>
          </DialogHeader>
          {unitDialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Unit number *</Label>
                  <Input data-testid="unit-number" value={unitDialog.unit.unit_number || ''} onChange={(e) => setUnitDialog({ ...unitDialog, unit: { ...unitDialog.unit, unit_number: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
                <div>
                  <Label>Sq ft</Label>
                  <Input type="number" value={unitDialog.unit.sqft || ''} onChange={(e) => setUnitDialog({ ...unitDialog, unit: { ...unitDialog.unit, sqft: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Bedrooms</Label>
                  <Input type="number" value={unitDialog.unit.bedrooms || ''} onChange={(e) => setUnitDialog({ ...unitDialog, unit: { ...unitDialog.unit, bedrooms: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
                <div>
                  <Label>Bathrooms</Label>
                  <Input type="number" step="0.5" value={unitDialog.unit.bathrooms || ''} onChange={(e) => setUnitDialog({ ...unitDialog, unit: { ...unitDialog.unit, bathrooms: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-white">
                <input data-testid="unit-occupied" type="checkbox" checked={!!unitDialog.unit.occupied} onChange={(e) => setUnitDialog({ ...unitDialog, unit: { ...unitDialog.unit, occupied: e.target.checked } })} />
                Occupied
              </label>
              <div>
                <Label>Tenant name</Label>
                <Input data-testid="unit-tenant-name" value={unitDialog.unit.tenant_name || ''} onChange={(e) => setUnitDialog({ ...unitDialog, unit: { ...unitDialog.unit, tenant_name: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Tenant phone</Label>
                  <Input value={unitDialog.unit.tenant_phone || ''} onChange={(e) => setUnitDialog({ ...unitDialog, unit: { ...unitDialog.unit, tenant_phone: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
                <div>
                  <Label>Tenant email</Label>
                  <Input type="email" value={unitDialog.unit.tenant_email || ''} onChange={(e) => setUnitDialog({ ...unitDialog, unit: { ...unitDialog.unit, tenant_email: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={2} value={unitDialog.unit.notes || ''} onChange={(e) => setUnitDialog({ ...unitDialog, unit: { ...unitDialog.unit, notes: e.target.value } })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-[#404040] text-white" onClick={() => setUnitDialog(null)} disabled={busy}>Cancel</Button>
            <Button data-testid="save-unit-submit" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]" disabled={busy} onClick={saveUnit}>{unitDialog?.mode === 'add' ? 'Add unit' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirmation */}
      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArchiveIcon className="w-5 h-5 text-red-400" /> Archive property?</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">
              The property will be hidden from active lists but can be restored. NexFlow keeps the full version history. Hard-delete is super-admin only.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="border-[#404040] text-white" onClick={() => setArchiveOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-archive-property" className="bg-red-600 text-white hover:bg-red-700" onClick={archive} disabled={busy}>Archive</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyDetail;
